/**
 * Storage capability collector (P3).
 *
 * Reports which client-side storage layers are usable plus quota/usage and
 * whether our persistent `true_device_id` was already present. Storage
 * availability is a strong incognito / hardened-browser signal.
 */

import type { StorageSignals } from "../types.ts";
import { tryCatch } from "../util/async.ts";

function testLocalStorage(): boolean {
  return tryCatch(() => {
    const k = "__gs_t";
    window.localStorage.setItem(k, "1");
    window.localStorage.removeItem(k);
    return true;
  }, false);
}

function testSessionStorage(): boolean {
  return tryCatch(() => {
    const k = "__gs_t";
    window.sessionStorage.setItem(k, "1");
    window.sessionStorage.removeItem(k);
    return true;
  }, false);
}

function testIndexedDb(): boolean {
  return tryCatch(() => typeof indexedDB !== "undefined" && indexedDB !== null, false);
}

function testCacheApi(): boolean {
  return tryCatch(() => typeof caches !== "undefined" && caches !== null, false);
}

async function estimateQuota(): Promise<{ quota: number | null; usage: number | null }> {
  return await new Promise((resolve) => {
    try {
      const storage = (navigator as unknown as {
        storage?: { estimate?: () => Promise<{ quota?: number; usage?: number }> };
      }).storage;
      if (!storage?.estimate) return resolve({ quota: null, usage: null });
      storage.estimate()
        .then((e) => resolve({ quota: e.quota ?? null, usage: e.usage ?? null }))
        .catch(() => resolve({ quota: null, usage: null }));
      setTimeout(() => resolve({ quota: null, usage: null }), 500);
    } catch {
      resolve({ quota: null, usage: null });
    }
  });
}

/** `persistentIdPresent` is supplied by the device collector (triple-store read). */
export async function collectStorage(persistentIdPresent: boolean | null): Promise<StorageSignals> {
  const { quota, usage } = await estimateQuota();
  return {
    cookies_enabled: tryCatch(() => navigator.cookieEnabled, null),
    local_storage: testLocalStorage(),
    session_storage: testSessionStorage(),
    indexed_db: testIndexedDb(),
    cache_api: testCacheApi(),
    quota_bytes: quota,
    usage_bytes: usage,
    persistent_id_present: persistentIdPresent,
  };
}
